import { useEffect } from 'react';
import type { RefObject } from 'react';

function opensPopover(target: Element, popover: HTMLElement): boolean {
	const invoker: Element | null = target.closest('[popovertarget]');

	return invoker?.getAttribute('popovertarget') === popover.id;
}

/**
 *	A `popover="auto"` closes on a press outside it, and the browser then
 *	delivers that same press to whatever is underneath, so dismissing a panel
 *	over a table also opens the row under the pointer. This swallows the click
 *	that follows a press outside an open popover, so the press only closes it.
 *
 *	Only for a popover that dims the page behind it: there the page reads as
 *	out of reach, so a press on it should not land. A menu with no overlay
 *	leaves the page looking live, and a press there does what it looks like.
 *
 *	The popover's own trigger is left alone, since pressing it is how the
 *	browser toggles the popover shut.
 */
export function usePopoverClickGuard(
	popover: RefObject<HTMLElement | null>,
	enabled: boolean
): void {
	useEffect(() => {
		if (!enabled) {
			return;
		}

		let armed: boolean = false;

		function handlePointerDown(event: PointerEvent): void {
			const element: HTMLElement | null = popover.current;
			const target: EventTarget | null = event.target;

			armed =
				element !== null &&
				target instanceof Element &&
				element.matches(':popover-open') &&
				!element.contains(target) &&
				!opensPopover(target, element);
		}

		function handleClick(event: MouseEvent): void {
			if (!armed) {
				return;
			}

			armed = false;
			event.preventDefault();
			event.stopPropagation();
		}

		window.addEventListener('pointerdown', handlePointerDown, true);
		window.addEventListener('click', handleClick, true);

		return () => {
			window.removeEventListener('pointerdown', handlePointerDown, true);
			window.removeEventListener('click', handleClick, true);
		};
	}, [popover, enabled]);
}
